import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import EazyLogo from './EazyLogo';
import { useLanguage } from './LanguageContext';
import { el as elStrings, en as enStrings } from './i18n/app';
import { useNotificationNav } from './NotificationContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000';
const NOTIF_ITEM_HEIGHT = 76; // approx. row height (padding + icon/title/message/timestamp)
const NOTIF_VISIBLE_ITEMS = 3;

interface AppNotification {
  id: string;
  type: 'welcome' | 'task_assigned' | 'invited';
  organization_id: string;
  organization_name: string | null;
  project_id: string | null;
  project_name: string | null;
  task_id: string | null;
  task_title: string | null;
  read_at: string | null;
  created_at: string;
}

const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  pm: 'Project Manager',
  pmo: 'PMO',
  coordinator: 'Project Coordinator',
  guest_user: 'Guest User',
  super_admin: 'Super Admin',
};

interface AppHeaderProps {
  user: any;
  onHome?: () => void;
  onProfileEdit?: () => void;
  onLogout?: () => void;
  onOrgSettings?: () => void;
  onSuperAdmin?: () => void;
  onCreateOrg?: () => void;
}

const AppHeader: React.FC<AppHeaderProps> = ({
  user, onHome, onProfileEdit, onLogout, onOrgSettings, onSuperAdmin, onCreateOrg,
}) => {
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const ref = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);
  const { language, setLanguage } = useLanguage();
  const L = language === 'en' ? enStrings : elStrings;
  const NL = L.notifications;
  const { navigate: navigateFromNotification } = useNotificationNav();
  const unreadCount = notifications.filter(n => !n.read_at).length;

  useEffect(() => {
    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    axios.get(`${API}/notifications`, { headers })
      .then(res => setNotifications(res.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setNotifOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const markAllRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
    axios.put(`${API}/notifications/read-all`, {}, { headers }).catch(() => {});
  };

  const handleNotificationClick = (n: AppNotification) => {
    if (!n.read_at) {
      setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x));
      axios.put(`${API}/notifications/${n.id}/read`, {}, { headers }).catch(() => {});
    }
    setNotifOpen(false);
    if (n.type === 'task_assigned' && n.project_id && n.task_id) {
      navigateFromNotification({
        organization_id: n.organization_id,
        project_id: n.project_id,
        project_name: n.project_name || '',
        task_id: n.task_id,
      });
    } else if (n.type === 'invited') {
      navigateFromNotification({
        organization_id: n.organization_id,
        project_id: n.project_id || undefined,
        project_name: n.project_name || undefined,
      });
    }
  };

  const notificationIcon = (n: AppNotification) => {
    if (n.type === 'welcome') return '👋';
    if (n.type === 'invited') return '📁';
    return '📋';
  };

  const notificationTitle = (n: AppNotification) => {
    if (n.type === 'welcome') return NL.welcomeTitle;
    if (n.type === 'invited') return n.project_id ? NL.invitedProjectTitle : NL.invitedOrgTitle;
    return NL.taskAssignedTitle;
  };

  const notificationMessage = (n: AppNotification) => {
    if (n.type === 'welcome') return NL.welcomeMessage;
    if (n.type === 'invited') {
      return n.project_id
        ? NL.invitedProjectMessage(n.project_name || '', n.organization_name || '')
        : NL.invitedOrgMessage(n.organization_name || '');
    }
    return NL.taskAssignedMessage(n.task_title || '', n.project_name || '');
  };

  const timeAgo = (iso: string) => {
    const diffMs = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return language === 'el' ? 'μόλις τώρα' : 'just now';
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    return `${Math.floor(hours / 24)}d`;
  };

  const item = (icon: string, label: string, onClick: () => void, danger = false) => (
    <button
      key={label}
      onClick={() => { setOpen(false); onClick(); }}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        width: '100%', padding: '9px 12px', borderRadius: 8,
        background: 'transparent', border: 'none',
        cursor: 'pointer', fontSize: 14,
        color: danger ? '#ef4444' : '#374151',
        fontFamily: 'inherit',
      }}
      onMouseEnter={e => (e.currentTarget.style.background = danger ? '#fef2f2' : '#f3f4f6')}
      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
    >
      <span style={{ fontSize: 16 }}>{icon}</span>
      <span>{label}</span>
    </button>
  );

  return (
    <div style={{
      backgroundColor: '#1a1a2e',
      padding: '0 32px', height: 64,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      flexShrink: 0,
      position: 'sticky', top: 0, zIndex: 100,
    }}>
      {/* Left: logo */}
      <div onClick={onHome} style={{ cursor: onHome ? 'pointer' : 'default', userSelect: 'none' }}>
        <EazyLogo size="md" theme="dark" />
      </div>

      {/* Right: notifications + user menu + language switcher */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>

        <div style={{ position: 'relative' }} ref={notifRef}>
          <button
            onClick={() => setNotifOpen(!notifOpen)}
            aria-label={NL.title}
            style={{
              position: 'relative',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 44, height: 44,
              background: notifOpen ? 'rgba(255,255,255,0.12)' : 'transparent',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: '50%', cursor: 'pointer',
            }}
            onMouseEnter={e => { if (!notifOpen) e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
            onMouseLeave={e => { if (!notifOpen) e.currentTarget.style.background = 'transparent'; }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            {unreadCount > 0 && (
              <span style={{
                position: 'absolute', top: 8, right: 9,
                width: 9, height: 9, borderRadius: '50%',
                background: '#ef4444', border: '2px solid #1a1a2e',
              }} />
            )}
          </button>

          {notifOpen && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 8px)', right: 0,
              background: '#fff', borderRadius: 12,
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
              border: '1px solid #e5e7eb',
              width: 340, zIndex: 1000,
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
            }}>
              <div style={{
                padding: '12px 16px', borderBottom: '1px solid #f3f4f6',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e' }}>{NL.title}</span>
                {unreadCount > 0 && (
                  <button
                    onClick={markAllRead}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: '#4f46e5', fontSize: 12, fontWeight: 500, fontFamily: 'inherit',
                    }}
                  >
                    {NL.markAllRead}
                  </button>
                )}
              </div>

              <div style={{ maxHeight: NOTIF_VISIBLE_ITEMS * NOTIF_ITEM_HEIGHT, overflowY: 'auto' }}>
                {notifications.length === 0 && (
                  <div style={{ padding: '32px 16px', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                    {NL.empty}
                  </div>
                )}
                {notifications.map(n => (
                  <button
                    key={n.id}
                    onClick={() => handleNotificationClick(n)}
                    style={{
                      display: 'flex', width: '100%', textAlign: 'left',
                      gap: 10, padding: '12px 16px', border: 'none', cursor: 'pointer',
                      background: n.read_at ? 'transparent' : '#f5f3ff',
                      borderLeft: n.read_at ? '3px solid transparent' : '3px solid #4f46e5',
                      borderBottom: '1px solid #f3f4f6', fontFamily: 'inherit',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = n.read_at ? '#f9fafb' : '#ede9fe')}
                    onMouseLeave={e => (e.currentTarget.style.background = n.read_at ? 'transparent' : '#f5f3ff')}
                  >
                    <span style={{ fontSize: 16, flexShrink: 0 }}>{notificationIcon(n)}</span>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e' }}>
                        {notificationTitle(n)}
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                        {notificationMessage(n)}
                      </div>
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>{timeAgo(n.created_at)}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div style={{ position: 'relative' }} ref={ref}>
          <button
            onClick={() => setOpen(!open)}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              background: open ? 'rgba(255,255,255,0.12)' : 'transparent',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: 999, padding: '6px 14px 6px 8px',
              cursor: 'pointer', transition: 'background 0.15s',
              height: 44,
            }}
          >
            <div style={{
              width: 32, height: 32, borderRadius: '50%',
              background: '#4f46e5', color: 'white',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 700, flexShrink: 0,
            }}>
              {user.full_name?.charAt(0).toUpperCase()}
            </div>
            <div style={{ textAlign: 'left' }}>
              <div style={{ color: 'white', fontSize: 13, fontWeight: 500, lineHeight: 1.2 }}>{user.full_name}</div>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, lineHeight: 1.2 }}>
                {ROLE_LABELS[user.role] ?? user.role}
              </div>
            </div>
            <span style={{
              color: 'rgba(255,255,255,0.5)', fontSize: 10, marginLeft: 2,
              transform: open ? 'rotate(180deg)' : 'rotate(0)',
              transition: 'transform 0.2s', display: 'inline-block',
            }}>▼</span>
          </button>

          {open && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 8px)', right: 0,
              background: '#fff', borderRadius: 12,
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
              border: '1px solid #e5e7eb',
              minWidth: 220, zIndex: 1000, overflow: 'hidden',
            }}>
              <div style={{ padding: '14px 16px', borderBottom: '1px solid #f3f4f6', background: '#fafafa' }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e' }}>{user.full_name}</div>
                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{user.email}</div>
              </div>

              <div style={{ padding: 6 }}>
                {onHome        && item('🏠', L.header.home, onHome)}
                {onProfileEdit && item('🔑', L.header.changePassword, onProfileEdit)}
                {item('💬', L.header.giveFeedback, () => {
                  const subject = encodeURIComponent(`Feedback από ${user.full_name} (${user.email})`);
                  window.open(`mailto:noreply@eazyproject.cloud?subject=${subject}`, '_blank');
                })}
                {onOrgSettings && ['admin', 'org_admin'].includes(user.role) &&
                  item('⚙️', L.header.orgSettings, onOrgSettings)}
                {onCreateOrg && user.role !== 'super_admin' &&
                  !user.available_orgs?.some((o: any) => o.role === 'admin') &&
                  item('🏢', L.header.createOrg, onCreateOrg)}
                {onSuperAdmin  && user.role === 'super_admin' &&
                  item('🛡️', L.header.superAdmin, onSuperAdmin)}

                {onLogout && (
                  <>
                    <div style={{ height: 1, background: '#f3f4f6', margin: '4px 0' }} />
                    {item('🚪', L.header.logout, onLogout, true)}
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Language switcher */}
        <div style={{ position: 'relative' }} ref={langRef}>
          <button
            onClick={() => setLangOpen(!langOpen)}
            style={{
              background: langOpen ? 'rgba(255,255,255,0.12)' : 'transparent',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: 999, padding: '0 14px',
              cursor: 'pointer', color: 'rgba(255,255,255,0.85)',
              fontSize: 12, fontWeight: 700, fontFamily: 'inherit',
              letterSpacing: '0.05em', height: 44,
              display: 'flex', alignItems: 'center',
            }}
          >
            {L.header.languageLabel}
          </button>
          {langOpen && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 8px)', right: 0,
              background: '#fff', borderRadius: 10,
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
              border: '1px solid #e5e7eb',
              minWidth: 130, zIndex: 1000, overflow: 'hidden', padding: 4,
            }}>
              {(['el', 'en'] as const).map(lang => (
                <button key={lang} onClick={() => { setLanguage(lang); setLangOpen(false); }}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    width: '100%', padding: '8px 12px', borderRadius: 7,
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    fontSize: 13, color: language === lang ? '#4f46e5' : '#374151',
                    fontWeight: language === lang ? 600 : 400, fontFamily: 'inherit',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#f3f4f6')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  <span>{L.header.languageMenu[lang]}</span>
                  {language === lang && <span style={{ fontSize: 11 }}>✓</span>}
                </button>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default AppHeader;
